// What the workspace knows, and the rules it reads off it. The rules are the standalone editor's (editPage.ts), with the
// fixes from live testing: a page's state is "Needs approval", "Approved" or "Changed since approval" rather than every
// section reading "New section", and a site that has never been published says "Not live yet".

import { useQuery, useQueryClient, keepPreviousData, type QueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  isNotDeployed, workspaceApi, WorkspaceApiError,
  type DiffResponse, type LoadedPageState, type PageSummary, type PreviewTicket, type SharedResponse, type ReviewRole, type SiteTask, type TaskTeam, type TasksResponse, type WorkspaceMe,
} from "./api";
import { retryOnceTransient, RETRY_DELAY_MS } from "./connection";
import { changeSignature, indexDiff, pageChanges, type IndexedDiff, type PageChange } from "./diff";
import type { Tone } from "./ui";

export const DEFAULT_TEAMS: { id: TaskTeam; name: string }[] = [
  { id: "mkt", name: "Marketing" }, { id: "cmp", name: "Compliance" }, { id: "legal", name: "Legal" }, { id: "ai", name: "AI assistant" }, { id: "setup", name: "Setup" },
];
export const TASK_KINDS: Record<string, string> = {
  change: "Change", page: "New page", section: "New section", component: "New component", wording: "New wording", review: "Review", fix: "Fix", flag: "Flag", setup: "Setup",
};
export const STAGE_NAMES: Record<string, string> = { team: "Team review", compliance: "Compliance" };
export const STAGE_STATUS: Record<string, string> = {
  approved: "Approved", missing: "Needs approval", changes_requested: "Changes requested", changed: "Changed since approval", needs_confirmation: "Restored: needs confirmation",
};

/** A page's title without the " | Site name" tail. */
export const shortTitle = (p: { title?: string; path: string } | null | undefined) =>
  p ? String(p.title || p.path).split(/\s+[|·]\s+/)[0].trim() || p.path : "";

/** A page's name in a list of the site's pages: the home page is "Home" (its title usually leads with the site's name). */
export const pageName = (p: { title?: string; path: string } | null | undefined) => (p?.path === "/" ? "Home" : shortTitle(p));

export { personName } from "./reviewModel";

export const isApproved = (p: PageSummary) => p.stages.length > 0 && p.stages.every((s) => s.status === "approved");

/** Waiting for someone: differs from (or is missing from) the live site and not approved, or a restore to confirm. */
export const isPending = (p: PageSummary) => (p.live !== "same" && !isApproved(p)) || p.stages.some((s) => s.status === "needs_confirmation");

export function pageStatus(p: PageSummary | null | undefined): { label: string; tone: Tone } {
  if (!p) return { label: "Draft", tone: "neutral" };
  const st = p.stages;
  if (st.some((s) => s.status === "changes_requested")) return { label: "Changes requested", tone: "danger" };
  if (st.some((s) => s.status === "needs_confirmation")) return { label: "Needs confirmation", tone: "warning" };
  if (p.live === "same") return { label: "Live", tone: "neutral" };
  if (st.some((s) => s.status === "changed")) return { label: "Changed since approval", tone: "warning" };
  if (isApproved(p)) return { label: "Approved", tone: "success" };
  return { label: "Needs approval", tone: "info" };
}

export interface Me {
  name: string;
  roles: ReviewRole[];
  teams: TaskTeam[];
  team: TaskTeam;
  canEdit: boolean;
  canPublish: boolean;
  /** Compliance or team reviewer: their main job here is approving, not editing. */
  reviews: boolean;
}

export function deriveMe(me: WorkspaceMe | null | undefined, page: LoadedPageState | null, tasks: TasksResponse | null, email: string | undefined): Me {
  const roles = new Set<ReviewRole>((page?.your_roles ?? []) as ReviewRole[]);
  for (const r of me?.roles ?? []) if (r === "drafter" || r === "reviewer" || r === "compliance") roles.add(r);
  if (me?.can.review_compliance) roles.add("compliance");
  if (me?.can.review_team) roles.add("reviewer");
  const teams = (me?.teams?.length ? me.teams : tasks?.my_teams?.length ? tasks.my_teams : [roles.has("compliance") ? "cmp" : "mkt"]) as TaskTeam[];
  const first = (me?.user.name || me?.user.email_local || (email ?? "").split("@")[0].split(/[._+-]/)[0] || "").trim();
  return {
    name: first ? first.charAt(0).toUpperCase() + first.slice(1) : "You",
    roles: [...roles],
    teams,
    team: teams[0],
    canEdit: me ? me.can.edit : !!page?.can_edit,
    canPublish: me ? me.can.publish : !!page?.can_publish,
    reviews: teams[0] === "cmp" || teams[0] === "legal" || ((roles.has("compliance") || roles.has("reviewer")) && !roles.has("drafter")),
  };
}

export { myStages } from "./reviewModel";

/** Changes on a page for the Review list. A page that is not on the live site is ONE change: the whole page is new. */
export function reviewChanges(diff: IndexedDiff | null, p: PageSummary): PageChange[] {
  if (p.live === null) {
    const sections = diff?.pages[p.path]?.sections ?? [];
    const n = sections.length;
    // ins carries the page's text only so risk can be read off it; the note is what is shown.
    const text = sections.map((s) => s.after || "").join(" ").slice(0, 20000);
    return [{ id: p.path + "#new", section: "", comp: "Whole page", kind: "New page", pre: "", del: "", ins: text, post: "", hl: "", note: n ? `New page with ${n} section${n === 1 ? "" : "s"}. The whole page is reviewed as one.` : "New page. The whole page is reviewed as one." }];
  }
  return pageChanges(diff, p.path);
}

export function groupPages(pages: PageSummary[]) {
  const out = new Map<string, PageSummary[]>([["Main", []]]);
  const byPath = new Map(pages.map((p) => [p.path, p]));
  for (const p of pages) {
    const seg = p.path.split("/").filter(Boolean);
    let g = "Main";
    if (seg.length > 1) { const parent = byPath.get("/" + seg[0]); g = parent ? shortTitle(parent) : seg[0].replace(/[-_]+/g, " ").replace(/^./, (c) => c.toUpperCase()); }
    if (!out.has(g)) out.set(g, []);
    out.get(g)!.push(p);
  }
  // The home page leads its group, whatever order the pages arrive in.
  const main = out.get("Main")!;
  const home = main.findIndex((p) => p.path === "/");
  if (home > 0) main.unshift(...main.splice(home, 1));
  return [...out].filter(([, v]) => v.length);
}

/* ───────────────────────── loading ───────────────────────── */

export const wsKeys = {
  me: (j: string) => ["ws", j, "me"] as const,
  page: (j: string, path: string) => ["ws", j, "page", path] as const,
  diff: (j: string, hash: string | null) => ["ws", j, "diff", hash] as const,
  shared: (j: string, hash: string | null) => ["ws", j, "shared", hash] as const,
  tasks: (j: string) => ["ws", j, "tasks"] as const,
  ticket: (j: string, path: string, hash: string | null) => ["ws", j, "ticket", path, hash] as const,
  versions: (j: string, path: string) => ["ws", j, "versions", path] as const,
  review: (j: string) => ["ws", j, "review"] as const,
};

const retryUnlessFinal = (n: number, e: unknown) => n < 1 && !isNotDeployed(e) && !((e as { status?: number })?.status && [401, 403, 404].includes((e as { status: number }).status));
/** The editor's own reads (who I am, the page, its preview ticket): a 401 or 5xx in an upstream blip is retried once
 *  after ~1.5 s before the canvas says anything (connection.ts). */
const transient = { retry: retryOnceTransient, retryDelay: RETRY_DELAY_MS } as const;

/*
 * Reads that start before the draft's hash is known. The diff and the shared-text index are keyed by the draft's hash
 * (so an edit refetches them), which used to mean waiting for the page read (the slowest call) before asking for
 * either. They are asked for at once instead, alongside workspace/me, the page and the tasks, and the hash-keyed query
 * takes the answer when the hash lands, provided it is for that draft. Each early read is used once, within 30 s.
 */
const EARLY_MS = 30_000;
const early = new Map<string, { at: number; p: Promise<unknown> }>();
function startEarly(kind: "diff" | "shared", journey: string, run: () => Promise<unknown>) {
  const k = `${kind}|${journey}`;
  const e = early.get(k);
  if (e && Date.now() - e.at < EARLY_MS) return;
  const p = run();
  p.catch(() => undefined);
  early.set(k, { at: Date.now(), p });
}
function takeEarly<T>(kind: "diff" | "shared", journey: string): Promise<T> | null {
  const k = `${kind}|${journey}`;
  const e = early.get(k);
  early.delete(k);
  return e && Date.now() - e.at < EARLY_MS ? (e.p as Promise<T>) : null;
}
const hasAny = (qc: QueryClient, key: readonly unknown[]) => qc.getQueriesData({ queryKey: key }).some(([, v]) => v !== undefined);

/** The draft-wide reads (diff, shared index), started now unless this journey already has them. */
function startDraftReads(qc: QueryClient, journey: string) {
  if (!hasAny(qc, ["ws", journey, "diff"])) startEarly("diff", journey, () => workspaceApi.diff(journey));
  if (!hasAny(qc, ["ws", journey, "shared"])) startEarly("shared", journey, () => workspaceApi.shared(journey));
}

const readMe = (journey: string) => ({ queryKey: wsKeys.me(journey), queryFn: ({ signal }: { signal: AbortSignal }) => workspaceApi.me(journey, signal), staleTime: 60_000 });
/**
 * The page read gives up after PAGE_TIMEOUT_MS (a cold read measured 10 s on 20 Oct), so the canvas never sits on
 * "Opening…" for ever: it says the server could not be reached, retries on its own and offers Retry.
 */
export const PAGE_TIMEOUT_MS = 15_000;
export async function withTimeout<T>(ms: number, signal: AbortSignal | undefined, run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctl = new AbortController();
  let late = false;
  const timer = setTimeout(() => { late = true; ctl.abort(); }, ms);
  const stop = () => ctl.abort();
  signal?.addEventListener("abort", stop);
  try { return await run(ctl.signal); }
  catch (e) {
    if (late) throw new WorkspaceApiError(408, "timeout", "The website took too long to answer.");
    throw e;
  } finally { clearTimeout(timer); signal?.removeEventListener("abort", stop); }
}
const readPage = (journey: string, path: string) => ({ queryKey: wsKeys.page(journey, path), queryFn: ({ signal }: { signal: AbortSignal }) => withTimeout(PAGE_TIMEOUT_MS, signal, (s) => workspaceApi.page(journey, path, s)) });
const readTasks = (journey: string) => ({ queryKey: wsKeys.tasks(journey), queryFn: ({ signal }: { signal: AbortSignal }) => workspaceApi.tasks(journey, signal) });

/**
 * Whether this browser already knows the journey has no draft: the Pages list's read of it (GET /api/journey/pages,
 * artifact_hash null, which is the same `!j.preview` the page read answers "no draft" on), or an earlier page read
 * with no candidate. "none" lets the editor open on "No draft to edit yet" at once, while its own page read confirms.
 */
export function knownDraft(qc: QueryClient, journey: string): "none" | "some" | null {
  const said = (v: unknown): "none" | "some" | null => {
    const d = (v as { draft?: unknown } | undefined)?.draft;
    return d === null ? "none" : typeof d === "string" ? "some" : null;
  };
  const reads = qc.getQueriesData<{ page?: unknown; candidate?: unknown }>({ queryKey: ["ws", journey, "page"] }).map(([, v]) => v);
  if (reads.some((r) => r?.page)) return "some";
  if (reads.some((r) => r && r.candidate === null)) return "none";
  const one = said(qc.getQueryData(["cms-journey-pages", journey]));
  if (one) return one;
  const all = qc.getQueryData<Map<string, unknown>>(["cms-journey-pages-all"]);
  return all instanceof Map ? said(all.get(journey)) : null;
}

/**
 * The editor has no draft to show: the page read said so, or (until it answers) the Pages list already did. Never
 * while the page read has failed: that is the connection's state, with its own Retry.
 */
export function useNoDraft(journey: string, page: { data?: { page?: unknown } | null; isError: boolean }): boolean {
  const qc = useQueryClient();
  const [hint] = useState(() => knownDraft(qc, journey));
  if (page.isError) return false;
  if (page.data) return !page.data.page;
  return hint === "none";
}

/**
 * Start everything the editor reads for a journey: on hover or focus of a link into the editor, and as the editor
 * mounts. Already-fresh reads are not asked again (prefetchQuery honours staleTime). Never throws.
 */
export function prefetchWorkspace(qc: QueryClient, journey: string, path = "/") {
  void qc.prefetchQuery(readMe(journey));
  void qc.prefetchQuery({ ...readPage(journey, path), staleTime: 30_000 });
  void qc.prefetchQuery({ ...readTasks(journey), staleTime: 30_000 });
  startDraftReads(qc, journey);
}

/**
 * Everything the workspace reads, each query on its own so the canvas can show as soon as the page and its ticket
 * arrive while the panels fill in as the diff, shared-text index and tasks come back (progressive load). All five
 * start together: none waits for another (the diff and shared index through startDraftReads).
 */
export function useWorkspaceData(journey: string, path: string) {
  const qc = useQueryClient();
  // Before the queries subscribe (in their effects), so the draft-wide reads leave in the same tick as the rest.
  useState(() => startDraftReads(qc, journey));
  const me = useQuery({ ...readMe(journey), ...transient });
  const page = useQuery({ ...readPage(journey, path), ...transient, placeholderData: keepPreviousData });
  const loaded = page.data && page.data.page ? (page.data as LoadedPageState) : null;
  const hash = loaded?.artifact_hash ?? null;
  const diff = useQuery({
    queryKey: wsKeys.diff(journey, hash),
    queryFn: async ({ signal }) => {
      const pre = await takeEarly<DiffResponse>("diff", journey)?.catch(() => null);
      // An early answer for another draft (an edit landed in between) is dropped and asked again.
      return indexDiff(pre && (!pre.draft || pre.draft === hash) ? pre : await workspaceApi.diff(journey, signal));
    },
    enabled: !!hash, retry: retryUnlessFinal, staleTime: Infinity,
  });
  const shared = useQuery({
    queryKey: wsKeys.shared(journey, hash),
    queryFn: async ({ signal }) => {
      const pre = await takeEarly<SharedResponse>("shared", journey)?.catch(() => null);
      return pre && (!pre.artifact_hash || pre.artifact_hash === hash) ? pre : workspaceApi.shared(journey, signal);
    },
    enabled: !!hash, retry: retryUnlessFinal, staleTime: Infinity,
  });
  const tasks = useQuery({ ...readTasks(journey), retry: retryUnlessFinal });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ["ws", journey], refetchType: "active" });
  }, [qc, journey]);
  const refreshTasks = useCallback(() => qc.invalidateQueries({ queryKey: wsKeys.tasks(journey) }), [qc, journey]);

  return { me, page, loaded, diff, shared, tasks, refresh, refreshTasks };
}

/** When a ticket lapses, in ms (the API has said seconds, ms and ISO at different times). NaN when it cannot say. */
export function ticketExpiry(t: Pick<PreviewTicket, "expires_at">): number {
  const exp = t.expires_at;
  return typeof exp === "number" ? (exp < 1e12 ? exp * 1000 : exp) : Date.parse(exp);
}
/** A ticket is used only with more than a minute left: a frame opened on it has to load before it lapses. */
export const ticketFresh = (t: PreviewTicket | null | undefined, now = Date.now()) => !!t?.url && ticketExpiry(t) - now > 60_000;

/**
 * The iframe's src: a preview ticket (15 minutes, one page of one draft). The page API hands one over with the page
 * (`preview_ticket`, bloxx-analytics-mcp #583), so the frame can start the moment the page lands; only when it is
 * absent (an older server), has less than a minute left, or the frame never came up on it, is one minted with GET
 * /regulated/preview-ticket. It is minted again a minute before it expires. A refreshed ticket never replaces the src
 * of a canvas already showing that page and draft: that would reload the frame mid-edit. The src changes only when the
 * page or the draft does, which needs a new ticket anyway; `latest` is the freshest ticket, for "Open preview".
 *
 * `placeholder`: `page` is still the previous page's answer while this path's loads. With a server that carries
 * tickets the frame waits for this page's own answer (and ticket) rather than minting one beside it.
 *
 * There is no fallback to the cookie preview (preview_url): a cross-site frame cannot open it. Until the ticket route
 * is deployed, `unavailable` says so and the canvas shows a calm message.
 */
export function usePreviewUrl(journey: string, path: string, page: LoadedPageState | null, opts: { placeholder?: boolean } = {}) {
  const hash = page?.artifact_hash ?? null;
  const key = `${path}|${hash}`;
  // A ticket whose frame never came up (expired on arrival, refused): never used again, for this page and draft.
  const bad = useRef<{ key: string; url: string } | null>(null);
  const carried = page && page.page.path === path && page.artifact_hash === hash && ticketFresh(page.preview_ticket) && page.preview_ticket!.url !== (bad.current?.key === key ? bad.current.url : null) ? page.preview_ticket! : null;
  const carriedRef = useRef(carried);
  carriedRef.current = carried;
  const waitForOwn = !!opts.placeholder && !!page?.preview_ticket;
  const ticket = useQuery({
    queryKey: wsKeys.ticket(journey, path, hash),
    queryFn: ({ signal }) => carriedRef.current ?? workspaceApi.previewTicket(journey, path, hash!, signal),
    enabled: !!hash && !waitForOwn,
    ...transient,
    // A cached ticket that has lapsed (the editor closed and opened again) is fetched again, not shown.
    staleTime: (q) => (ticketFresh(q.state.data) ? Infinity : 0),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const at = ticketExpiry(data);
      return Number.isFinite(at) ? Math.max(15_000, at - Date.now() - 60_000) : false;
    },
  });
  const frozen = useRef<{ key: string; url: string } | null>(null);
  const usable = ticket.data && ticketFresh(ticket.data) && ticket.data.url !== (bad.current?.key === key ? bad.current.url : null) ? ticket.data : null;
  if (usable && frozen.current?.key !== key) frozen.current = { key, url: usable.url };
  const url = frozen.current?.key === key ? frozen.current.url : null;
  const refetch = ticket.refetch;
  /** The frame never came up on this ticket: mint a new one, once per page and draft. */
  const renew = useCallback(() => {
    if (!url || bad.current?.key === key) return;
    bad.current = { key, url };
    carriedRef.current = null;
    frozen.current = null;
    void refetch();
  }, [url, key, refetch]);
  const unavailable = ticket.isError && isNotDeployed(ticket.error);
  const retry = useCallback(() => { void refetch(); }, [refetch]);
  return { url, latest: usable?.url ?? url, pending: !!hash && !url && !ticket.isError, unavailable, error: ticket.isError && !unavailable ? (ticket.error as Error) : null, renew, retry };
}

/** Derived lists every tab reads. */
export function useDerived(loaded: LoadedPageState | null, diff: IndexedDiff | null, shared: { texts: Record<string, string[]>; components: Record<string, string[]> } | null, tasks: TasksResponse | null) {
  return useMemo(() => {
    const pages = loaded?.pages ?? [];
    const byPath = new Map(pages.map((p) => [p.path, p]));
    const openTasks: SiteTask[] = (tasks?.tasks ?? []).filter((t) => t.status !== "done" && t.status !== "cancelled");
    const pending = pages.filter(isPending);
    const reviewTaskFor = (path: string) => openTasks.find((t) => t.kind === "review" && t.page_path === path);
    const changesFor = (p: PageSummary) => reviewChanges(diff, p);
    /** Pages pending review that carry the same text change (for "Approve on all N"). */
    const sharedWith = (c: PageChange) => {
      const k = changeSignature(c);
      if (!k || !diff) return [];
      return pending.filter((p) => p.live !== null && pageChanges(diff, p.path).some((x) => changeSignature(x) === k)).map((p) => p.path);
    };
    const neverLive = pages.length > 0 && pages.every((p) => p.live === null);
    const name = (path: string) => shortTitle(byPath.get(path) ?? { path });
    return { pages, byPath, openTasks, pending, reviewTaskFor, changesFor, sharedWith, neverLive, name, shared: shared ?? { texts: {}, components: {} } };
  }, [loaded, diff, shared, tasks]);
}
export type Derived = ReturnType<typeof useDerived>;
